import styled, { css, keyframes } from 'styled-components'
import { COLORS } from 'consts/colors'
import { MOBILE_TAB_BAR_HEIGHT, SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { SIDEBAR_MOBILE_Z_INDEX } from 'consts/zIndex'

/* Mobile bottom-bar geometry. Every tab gets the SAME fixed, squarish width
   (~5 visible per viewport, the rest scroll) instead of sizing to its label, so
   the bar reads as an even row. `MOBILE_TAB_INSET` is the wrapper's horizontal
   padding: the 1px divider sits on the wrapper's right edge, so an equal inset
   on both sides leaves the same gap to the left and to the right of it. */
const MOBILE_TAB_WIDTH = 76
const MOBILE_TAB_INSET = 6

/* The bar's vertical inset, and the tab height left over for it. The tabs are
   centred, so the visible space above the icon is (tab height - content) / 2
   plus this inset — which is why the bar height, not the padding split, is what
   gives the icons room to breathe. */
const MOBILE_BAR_INSET = 6
const MOBILE_TAB_HEIGHT = MOBILE_TAB_BAR_HEIGHT - MOBILE_BAR_INSET * 2

const selectPop = keyframes`
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.06);
  }
  100% {
    transform: scale(1);
  }
`

export const Sidebar = styled.aside`
  position: relative;
  width: 72px;
  padding: 0 6px 8px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  box-sizing: border-box;
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  z-index: ${SIDEBAR_MOBILE_Z_INDEX};
  @media only screen and (max-width: ${SMALL_DESKTOP_SIZE_BREAKPOINT}px) {
    width: auto;
    height: ${MOBILE_TAB_BAR_HEIGHT}px;
    padding: ${MOBILE_BAR_INSET}px;
    flex-direction: row;
    position: relative;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    /* Full-bleed bottom bar on mobile — flat white with a top divider. */
    border-radius: 0;
    background-color: ${COLORS.surface};
    border-top: 1px solid ${COLORS.borderLayout};
  }
`

export const SidebarLogoWrapper = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media only screen and (max-width: ${SMALL_DESKTOP_SIZE_BREAKPOINT}px) {
    display: none;
  }

  & > img {
    width: 24px;
    height: 32px;
  }
`

export const SidebarItemWrapper = styled.div<{ $isActive: boolean }>`
  text-decoration: none;
  flex-shrink: 0;
  @media only screen and (max-width: ${SMALL_DESKTOP_SIZE_BREAKPOINT}px) {
    /* Fixed-width tabs (see MOBILE_TAB_WIDTH) — content-sized ones made the bar
       uneven, and the divider sat flush against the next tab's content. */
    flex: 0 0 ${MOBILE_TAB_WIDTH}px;
    width: ${MOBILE_TAB_WIDTH}px;
    padding: 0 ${MOBILE_TAB_INSET}px;
    box-sizing: border-box;
    border-right: 1px solid ${COLORS.borderLayout};

    &:last-child {
      border-right: none;
    }
  }
`

/* The tint "selected layer" — a single pill that glides between items. */
export const ActivePill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${COLORS.primaryTint};
  border-radius: 12px;
  pointer-events: none;
  transition:
    transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
    width 0.3s cubic-bezier(0.22, 1, 0.36, 1),
    height 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  @media only screen and (max-width: ${SMALL_DESKTOP_SIZE_BREAKPOINT}px) {
    border-radius: 10px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const SidebarItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 64px;
  border-radius: 12px;
  overflow: hidden;
  box-sizing: border-box;
  position: relative;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
  /* The Text component renders a p — these rules used to target span and so
     never applied, which is why the active label stayed dark next to a
     primary-tinted icon. Size/weight stay owned by the Text type prop (h5 =
     11px/500); only the state color lives here, since it needs $isActive. */
  & > p {
    color: ${COLORS.textSecondary};
    word-break: break-word;
    transition: color 0.2s ease;
  }

  & > svg path {
    fill: ${COLORS.textSecondary};
    transition: fill 0.2s ease;
  }

  ${({ $isActive }) =>
    $isActive
      ? css`
          cursor: default;
          animation: ${selectPop} 0.35s ease;

          & > p {
            color: ${COLORS.primary};
          }

          & > svg path {
            fill: ${COLORS.primary};
          }

          @media (prefers-reduced-motion: reduce) {
            animation: none;
          }
        `
      : css`
          cursor: pointer;
          &:hover {
            background-color: ${COLORS.fillSubtle};

            & > svg path {
              fill: ${COLORS.textPrimary};
            }

            & > p {
              color: ${COLORS.textPrimary};
            }
          }
        `}

  @media only screen and (max-width: ${SMALL_DESKTOP_SIZE_BREAKPOINT}px) {
    height: ${MOBILE_TAB_HEIGHT}px;
    border-radius: 10px;
    padding: 0 4px;
    /* Worst-case content is 21px icon + 4px gap + a 26px two-line label = 51px.
       The tab centres it, so the room above the icon is
       (MOBILE_TAB_HEIGHT - content) / 2 plus the bar's own inset — at the old 64px
       bar the two-line case came to ~6px and the icons read as flush against the
       bar's top divider. MOBILE_TAB_BAR_HEIGHT carries the extra height; don't
       shave it back without shrinking the content too. */
    gap: 4px;

    /* Fixed-width tabs can't grow for a long localized label ("Téléchargements",
       "Synthèse vocale"), so the label wraps to TWO lines rather than truncating
       to an ellipsis on one. The box is NOT given a fixed height: labels hug their
       own line count, so a one-line tab centres tighter than a two-line one and
       the icons sit slightly lower on it. line-height is pinned (not left at
       normal) so the two-line height stays exact regardless of resolved font. */
    & > p {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      width: 100%;
      line-height: 13px;
    }
  }
`

export const NewLabel = styled.img`
  width: 35px;
  position: absolute;
  top: 0;
  right: 0;
`
